(() => {
  const BANDING_LEVELS = ["8", "16", "32", "64", "128", "256", "smooth"];
  const FULLSCREEN_PATTERNS = ["vertical-bars", "horizontal-bars"];
  const PAGE_LOCK_CLASS = "grayscale-gradient-page-active";

  function clamp(value, min, max) {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return min;
    }

    return Math.min(max, Math.max(min, numericValue));
  }

  function normalizeBandingLevel(value) {
    return BANDING_LEVELS.includes(String(value)) ? String(value) : "256";
  }

  function normalizeShadowBackground(value) {
    return value === "lifted" ? "lifted" : "pure";
  }

  function normalizeFullscreenPattern(value) {
    return FULLSCREEN_PATTERNS.includes(String(value)) ? String(value) : "vertical-bars";
  }

  function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function requestNodeFullscreen(node) {
    if (node?.requestFullscreen) {
      return node.requestFullscreen();
    }

    if (node?.webkitRequestFullscreen) {
      return node.webkitRequestFullscreen();
    }

    return Promise.reject(new Error("Fullscreen API is unavailable."));
  }

  function exitBrowserFullscreen() {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }

    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }

    return Promise.resolve();
  }

  function lockPage() {
    document.documentElement.classList.add(PAGE_LOCK_CLASS);
    document.body.classList.add(PAGE_LOCK_CLASS);
  }

  function unlockPage() {
    document.documentElement.classList.remove(PAGE_LOCK_CLASS);
    document.body.classList.remove(PAGE_LOCK_CLASS);
  }

  function setupCanvas(canvas) {
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(280, rect.width || canvas.offsetWidth || 280);
    const height = Math.max(240, rect.height || canvas.offsetHeight || 240);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    const context = canvas.getContext("2d");
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    return { context, width, height };
  }

  function roundedRect(context, x, y, width, height, radius) {
    const safeRadius = Math.min(radius, width / 2, height / 2);

    context.beginPath();
    context.moveTo(x + safeRadius, y);
    context.lineTo(x + width - safeRadius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
    context.lineTo(x + width, y + height - safeRadius);
    context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
    context.lineTo(x + safeRadius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
    context.lineTo(x, y + safeRadius);
    context.quadraticCurveTo(x, y, x + safeRadius, y);
    context.closePath();
  }

  function getEffectiveLevels(bitDepth, levelValue) {
    if (levelValue === "smooth") {
      return bitDepth === "10bit" ? 1024 : 256;
    }

    const base = clamp(levelValue, 8, 256);
    return bitDepth === "10bit" ? Math.min(1024, base * 4) : base;
  }

  function getBandingLevelLabel(levelValue) {
    return levelValue === "smooth" ? "Smooth" : `${levelValue} levels`;
  }

  function getShadowMessage(rating) {
    if (rating === "excellent") {
      return {
        title: "0%-10% remains visible: excellent shadow detail.",
        copy: "The display chain is separating near-black tones well. Mini LED local dimming and OLED black control can still improve perceived contrast."
      };
    }

    if (rating === "mid") {
      return {
        title: "4%-10% visible: some dark detail is getting compressed.",
        copy: "Try Black Equalizer, verify SDR output depth, and check whether a lifted black background reveals the missing 0%-4% blocks."
      };
    }

    if (rating === "poor") {
      return {
        title: "Only 6%-10% visible: strong black crush is likely.",
        copy: "Dark scenes may hide enemies, texture detail, or low-light video information. Start with GPU range, gamma, and OSD shadow controls."
      };
    }

    return {
      title: "Use a dark room and normal daily brightness.",
      copy: "If the 0%-4% blocks disappear entirely, the display chain or panel tuning may be crushing shadow detail into black."
    };
  }

  function setReadout(node, title, copy) {
    if (!node) {
      return;
    }

    const titleNode = node.querySelector("strong");
    const copyNode = node.querySelector("span");

    if (titleNode) {
      titleNode.textContent = title;
    }

    if (copyNode) {
      copyNode.textContent = copy;
    }
  }

  function getQuantizedGray(progress, bitDepth, levelValue) {
    const normalizedProgress = clamp(progress, 0, 1);
    const levels = getEffectiveLevels(bitDepth, levelValue);
    const quantized = Math.round(normalizedProgress * (levels - 1)) / Math.max(1, levels - 1);
    return clamp(Math.round(quantized * 255), 0, 255);
  }

  function fillQuantizedRamp(context, x, y, width, height, orientation, levels) {
    const safeLevels = Math.max(2, Math.round(levels));
    const transform = typeof context.getTransform === "function" ? context.getTransform() : null;
    const dpr = Math.max(1, transform?.a || Math.min(window.devicePixelRatio || 1, 2));
    const axisStart = orientation === "vertical" ? y : x;
    const axisLength = orientation === "vertical" ? height : width;

    for (let index = 0; index < safeLevels; index += 1) {
      const startPixel = Math.round((axisStart + (index / safeLevels) * axisLength) * dpr);
      const endPixel = Math.round((axisStart + ((index + 1) / safeLevels) * axisLength) * dpr);
      const segmentSize = Math.max(0, endPixel - startPixel);

      if (segmentSize <= 0) {
        continue;
      }

      const progress = safeLevels === 1 ? 0 : index / (safeLevels - 1);
      const gray = clamp(Math.round(progress * 255), 0, 255);

      context.fillStyle = `rgb(${gray}, ${gray}, ${gray})`;

      if (orientation === "vertical") {
        context.fillRect(x, startPixel / dpr, width, segmentSize / dpr);
      } else {
        context.fillRect(startPixel / dpr, y, segmentSize / dpr, height);
      }
    }
  }

  function drawBandingCanvas(canvas, bitDepth, levelValue) {
    if (!canvas) {
      return;
    }

    const { context, width, height } = setupCanvas(canvas);
    const effectiveLevels = getEffectiveLevels(bitDepth, levelValue);
    const cardPadding = Math.max(18, width * 0.042);
    const innerWidth = width - cardPadding * 2;
    const horizontalHeight = Math.max(56, height * 0.22);
    const gap = Math.max(12, height * 0.04);
    const verticalHeight = height - cardPadding * 2 - horizontalHeight - gap - 28;
    const accent = bitDepth === "10bit" ? "#8ff0ff" : "#ffcf7d";
    const border = bitDepth === "10bit" ? "rgba(143, 240, 255, 0.26)" : "rgba(255, 207, 125, 0.26)";

    context.clearRect(0, 0, width, height);

    const background = context.createLinearGradient(0, 0, width, height);
    background.addColorStop(0, "#0f1115");
    background.addColorStop(1, "#07090d");
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);

    context.strokeStyle = border;
    context.lineWidth = 1;
    roundedRect(context, 0.5, 0.5, width - 1, height - 1, 18);
    context.stroke();

    context.fillStyle = "rgba(255, 255, 255, 0.62)";
    context.font = "700 11px Montserrat, Segoe UI, sans-serif";
    context.fillText("HORIZONTAL RAMP", cardPadding, cardPadding - 2);

    roundedRect(context, cardPadding, cardPadding + 8, innerWidth, horizontalHeight, 12);
    context.save();
    context.clip();
    fillQuantizedRamp(context, cardPadding, cardPadding + 8, innerWidth, horizontalHeight, "horizontal", effectiveLevels);
    context.restore();

    const verticalY = cardPadding + horizontalHeight + gap + 8;

    context.fillStyle = "rgba(255, 255, 255, 0.62)";
    context.fillText("VERTICAL RAMP", cardPadding, verticalY - 10);

    roundedRect(context, cardPadding, verticalY, innerWidth, verticalHeight, 12);
    context.save();
    context.clip();
    fillQuantizedRamp(context, cardPadding, verticalY, innerWidth, verticalHeight, "vertical", effectiveLevels);
    context.restore();

    context.fillStyle = accent;
    context.font = "800 13px Montserrat, Segoe UI, sans-serif";
    context.fillText(`${effectiveLevels.toLocaleString()} effective steps`, cardPadding, height - cardPadding + 2);

    context.fillStyle = "rgba(255, 255, 255, 0.62)";
    context.font = "700 11px Montserrat, Segoe UI, sans-serif";
    context.fillText(levelValue === "smooth" ? "Smooth reference mode" : `${levelValue}-level stress preset`, cardPadding + 144, height - cardPadding + 2);
  }

  function drawFullscreenBandingCanvas(canvas, bitDepth, levelValue, pattern) {
    if (!canvas) {
      return;
    }

    const { context, width, height } = setupCanvas(canvas);
    const effectiveLevels = getEffectiveLevels(bitDepth, levelValue);
    const orientation = pattern === "horizontal-bars" ? "vertical" : "horizontal";

    context.clearRect(0, 0, width, height);

    fillQuantizedRamp(context, 0, 0, width, height, orientation, effectiveLevels);
  }

  function drawShadowCanvas(canvas, backgroundMode) {
    if (!canvas) {
      return;
    }

    const { context, width, height } = setupCanvas(canvas);
    const pureBackground = backgroundMode === "pure";
    const outerPadding = Math.max(18, width * 0.03);
    const gap = Math.max(8, width * 0.012);
    const gridSize = Math.min(width - outerPadding * 2, height - outerPadding * 2);
    const cellSize = (gridSize - gap * 4) / 5;
    const startX = (width - gridSize) / 2;
    const startY = (height - gridSize) / 2;
    const values = [0, 2, 4, 6, 8, 10];
    const backgroundGray = pureBackground ? 0 : 6;

    context.clearRect(0, 0, width, height);
    context.fillStyle = `rgb(${backgroundGray}, ${backgroundGray}, ${backgroundGray})`;
    context.fillRect(0, 0, width, height);

    if (!pureBackground) {
      const glow = context.createRadialGradient(width * 0.5, height * 0.45, 0, width * 0.5, height * 0.45, width * 0.68);
      glow.addColorStop(0, "rgba(255, 255, 255, 0.045)");
      glow.addColorStop(1, "rgba(255, 255, 255, 0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);
    }

    context.fillStyle = "rgba(255, 255, 255, 0.76)";
    context.font = "700 12px Montserrat, Segoe UI, sans-serif";
    context.fillText("0%-10% SHADOW DETAIL GRID", outerPadding, outerPadding - 2);

    for (let row = 0; row < 5; row += 1) {
      for (let column = 0; column < 5; column += 1) {
        const index = row * 5 + column;
        const cellX = startX + column * (cellSize + gap);
        const cellY = startY + row * (cellSize + gap);
        const level = values[(row * 2 + column) % values.length];
        const gray = Math.round((level / 100) * 255);
        const digitGrayPercent = clamp(level <= 4 ? level + 2 : level - 1.4, 0, 10);
        const digitGray = Math.round((digitGrayPercent / 100) * 255);

        context.fillStyle = `rgb(${gray}, ${gray}, ${gray})`;
        roundedRect(context, cellX, cellY, cellSize, cellSize, 10);
        context.fill();

        context.strokeStyle = "rgba(255, 255, 255, 0.05)";
        context.lineWidth = 1;
        roundedRect(context, cellX + 0.5, cellY + 0.5, cellSize - 1, cellSize - 1, 10);
        context.stroke();

        context.fillStyle = `rgb(${digitGray}, ${digitGray}, ${digitGray})`;
        context.font = `800 ${Math.max(18, cellSize * 0.26)}px Montserrat, Segoe UI, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(String(index + 1), cellX + cellSize / 2, cellY + cellSize / 2);
      }
    }

    context.textAlign = "start";
    context.textBaseline = "alphabetic";
    context.fillStyle = "rgba(255, 255, 255, 0.66)";
    context.font = "700 12px Montserrat, Segoe UI, sans-serif";
    context.fillText(pureBackground ? "Pure black background" : "Lifted black background", outerPadding, height - outerPadding + 2);
  }

  function drawFullscreenShadowCanvas(canvas, backgroundMode) {
    if (!canvas) {
      return;
    }

    const { context, width, height } = setupCanvas(canvas);
    const pureBackground = backgroundMode === "pure";
    const values = [0, 2, 4, 6, 8, 10];
    const backgroundGray = pureBackground ? 0 : 6;
    const gridSize = Math.min(width * 0.72, height * 0.72);
    const gap = Math.max(14, gridSize * 0.03);
    const cellSize = (gridSize - gap * 4) / 5;
    const startX = (width - gridSize) / 2;
    const startY = Math.max(40, (height - gridSize) / 2 - height * 0.04);

    context.clearRect(0, 0, width, height);
    context.fillStyle = `rgb(${backgroundGray}, ${backgroundGray}, ${backgroundGray})`;
    context.fillRect(0, 0, width, height);

    if (!pureBackground) {
      const glow = context.createRadialGradient(width * 0.5, height * 0.42, 0, width * 0.5, height * 0.42, width * 0.72);
      glow.addColorStop(0, "rgba(255, 255, 255, 0.06)");
      glow.addColorStop(1, "rgba(255, 255, 255, 0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);
    }

    for (let row = 0; row < 5; row += 1) {
      for (let column = 0; column < 5; column += 1) {
        const index = row * 5 + column;
        const cellX = startX + column * (cellSize + gap);
        const cellY = startY + row * (cellSize + gap);
        const level = values[(row * 2 + column) % values.length];
        const gray = Math.round((level / 100) * 255);
        const digitGrayPercent = clamp(level <= 4 ? level + 2 : level - 1.4, 0, 10);
        const digitGray = Math.round((digitGrayPercent / 100) * 255);

        context.fillStyle = `rgb(${gray}, ${gray}, ${gray})`;
        roundedRect(context, cellX, cellY, cellSize, cellSize, Math.max(12, cellSize * 0.1));
        context.fill();

        context.strokeStyle = "rgba(255, 255, 255, 0.06)";
        context.lineWidth = 1;
        roundedRect(context, cellX + 0.5, cellY + 0.5, cellSize - 1, cellSize - 1, Math.max(12, cellSize * 0.1));
        context.stroke();

        context.fillStyle = `rgb(${digitGray}, ${digitGray}, ${digitGray})`;
        context.font = `800 ${Math.max(24, cellSize * 0.24)}px Montserrat, Segoe UI, sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(String(index + 1), cellX + cellSize / 2, cellY + cellSize / 2);
      }
    }
  }

  function initGrayscaleTool(node) {
    if (!node || node.dataset.grayscaleToolInitialized === "true") {
      return;
    }

    node.dataset.grayscaleToolInitialized = "true";

    const elements = {
      banding8Canvas: node.querySelector('[data-grayscale-canvas="banding-8bit"]'),
      banding10Canvas: node.querySelector('[data-grayscale-canvas="banding-10bit"]'),
      shadowCanvas: node.querySelector('[data-grayscale-canvas="shadow"]'),
      fullscreenBandingCanvas: node.querySelector('[data-grayscale-canvas="banding-fullscreen"]'),
      fullscreenShadowCanvas: node.querySelector('[data-grayscale-canvas="shadow-fullscreen"]'),
      bandingProfile: node.querySelector("[data-grayscale-banding-profile]"),
      bandingMeta: node.querySelector("[data-grayscale-banding-meta]"),
      bandingReadout: node.querySelector("[data-grayscale-banding-readout]"),
      shadowReadout: node.querySelector("[data-grayscale-shadow-readout]"),
      focusButtons: node.querySelectorAll("[data-grayscale-focus]"),
      levelButtons: node.querySelectorAll("[data-grayscale-level]"),
      shadowBackgroundButtons: node.querySelectorAll("[data-grayscale-shadow-background]"),
      ratingButtons: node.querySelectorAll("[data-grayscale-rating]"),
      bandingPanels: node.querySelectorAll("[data-grayscale-banding-panel]"),
      results: node.querySelector("[data-grayscale-results]"),
      resultsTitle: node.querySelector("[data-grayscale-results-title]"),
      resultsCopy: node.querySelector("[data-grayscale-results-copy]"),
      resultCards: node.querySelectorAll("[data-grayscale-result-group]"),
      announcer: node.querySelector("[data-grayscale-announcer]"),
      viewer: node.querySelector("[data-grayscale-viewer]"),
      viewerSurfaces: node.querySelectorAll("[data-grayscale-viewer-surface]"),
      viewerGroups: node.querySelectorAll("[data-grayscale-viewer-group]"),
      viewerRgb: node.querySelector("[data-grayscale-viewer-rgb]"),
      viewerLevelSelect: node.querySelector("[data-grayscale-fullscreen-level]"),
      viewerFocusSelect: node.querySelector("[data-grayscale-fullscreen-focus]"),
      viewerPatternSelect: node.querySelector("[data-grayscale-fullscreen-pattern]"),
      viewerShadowBackgroundSelect: node.querySelector("[data-grayscale-fullscreen-shadow-background]")
    };

    const state = {
      focus: "8bit",
      bandingLevel: normalizeBandingLevel(node.dataset.defaultBandingLevel),
      shadowBackground: normalizeShadowBackground(node.dataset.defaultShadowBackground),
      shadowRating: "baseline",
      resultMode: null,
      fullscreenModule: null,
      fullscreenScroll: null,
      fullscreenBandingPattern: "vertical-bars",
      fullscreenSampleGray: 128
    };

    function announce(message) {
      if (elements.announcer) {
        elements.announcer.textContent = message;
      }
    }

    function isModuleFullscreen(moduleName) {
      return state.fullscreenModule === moduleName && !elements.viewer?.hidden;
    }

    function rememberScrollPosition() {
      state.fullscreenScroll = {
        x: window.scrollX || window.pageXOffset || 0,
        y: window.scrollY || window.pageYOffset || 0
      };
    }

    function restoreScrollPosition() {
      const scroll = state.fullscreenScroll;

      if (!scroll) {
        return;
      }

      const restore = () => {
        window.scrollTo(scroll.x, scroll.y);
      };

      window.requestAnimationFrame(() => {
        restore();
        window.setTimeout(restore, 80);
        window.setTimeout(() => {
          restore();
          state.fullscreenScroll = null;
        }, 220);
      });
    }

    function setViewerSample(progress = 0.5) {
      state.fullscreenSampleGray = getQuantizedGray(progress, state.focus, state.bandingLevel);
    }

    function updateViewerRgb() {
      if (!elements.viewerRgb) {
        return;
      }

      const active = state.fullscreenModule === "banding";
      elements.viewerRgb.hidden = !active;

      if (!active) {
        return;
      }

      const gray = clamp(state.fullscreenSampleGray, 0, 255);
      elements.viewerRgb.textContent = `RGB(${gray}, ${gray}, ${gray})`;
    }

    function syncViewerState() {
      if (!elements.viewer) {
        return;
      }

      const activeMode = state.fullscreenModule;
      const active = Boolean(activeMode);

      elements.viewer.hidden = !active;
      elements.viewer.setAttribute("aria-hidden", String(!active));

      elements.viewerSurfaces.forEach((surface) => {
        surface.hidden = surface.dataset.grayscaleViewerSurface !== activeMode;
      });

      elements.viewerGroups.forEach((group) => {
        group.hidden = group.dataset.grayscaleViewerGroup !== activeMode;
      });

      if (elements.viewerLevelSelect) {
        elements.viewerLevelSelect.value = state.bandingLevel;
      }

      if (elements.viewerFocusSelect) {
        elements.viewerFocusSelect.value = state.focus;
      }

      if (elements.viewerPatternSelect) {
        elements.viewerPatternSelect.value = state.fullscreenBandingPattern;
      }

      if (elements.viewerShadowBackgroundSelect) {
        elements.viewerShadowBackgroundSelect.value = state.shadowBackground;
      }

      updateViewerRgb();
    }

    function syncFullscreenButtons() {
      node.querySelectorAll('[data-grayscale-action="fullscreen-banding"], [data-grayscale-action="fullscreen-shadow"]').forEach((button) => {
        const moduleName = button.dataset.grayscaleAction === "fullscreen-shadow" ? "shadow" : "banding";
        const active = isModuleFullscreen(moduleName);

        button.textContent = active ? "Exit Fullscreen" : "Fullscreen";
        button.setAttribute("aria-pressed", String(active));
      });
    }

    function renderFullscreenViewer() {
      if (state.fullscreenModule === "banding") {
        drawFullscreenBandingCanvas(elements.fullscreenBandingCanvas, state.focus, state.bandingLevel, state.fullscreenBandingPattern);
        updateViewerRgb();
      }

      if (state.fullscreenModule === "shadow") {
        drawFullscreenShadowCanvas(elements.fullscreenShadowCanvas, state.shadowBackground);
      }
    }

    function renderAll() {
      updateBandingPanels();
      drawBandingCanvas(elements.banding8Canvas, "8bit", state.bandingLevel);
      drawBandingCanvas(elements.banding10Canvas, "10bit", state.bandingLevel);
      drawShadowCanvas(elements.shadowCanvas, state.shadowBackground);
      renderFullscreenViewer();
    }

    function showViewer(moduleName) {
      state.fullscreenModule = moduleName;

      if (moduleName === "banding") {
        setViewerSample(0.5);
      }

      lockPage();
      syncViewerState();
      syncFullscreenButtons();
      renderAll();
    }

    function hideViewer(restoreScroll = true) {
      if (!state.fullscreenModule) {
        return;
      }

      state.fullscreenModule = null;
      unlockPage();
      syncViewerState();
      syncFullscreenButtons();

      if (restoreScroll) {
        restoreScrollPosition();
      }
    }

    function openModuleFullscreen(moduleName) {
      if (!elements.viewer || isModuleFullscreen(moduleName)) {
        return;
      }

      rememberScrollPosition();
      showViewer(moduleName);

      Promise.resolve(requestNodeFullscreen(elements.viewer)).catch(() => {});
    }

    function exitModuleFullscreen() {
      if (!state.fullscreenModule) {
        return;
      }

      if (getFullscreenElement() === elements.viewer) {
        Promise.resolve(exitBrowserFullscreen()).catch(() => {
          hideViewer();
        });
        return;
      }

      hideViewer();
    }

    function toggleModuleFullscreen(moduleName) {
      if (isModuleFullscreen(moduleName)) {
        exitModuleFullscreen();
      } else {
        openModuleFullscreen(moduleName);
      }
    }

    function updateBandingPanels() {
      elements.bandingPanels.forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.grayscaleBandingPanel === state.focus);
      });
    }

    function updateBandingControls() {
      elements.focusButtons.forEach((button) => {
        const active = button.dataset.grayscaleFocus === state.focus;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      elements.levelButtons.forEach((button) => {
        const active = button.dataset.grayscaleLevel === state.bandingLevel;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    }

    function updateShadowControls() {
      elements.shadowBackgroundButtons.forEach((button) => {
        const active = button.dataset.grayscaleShadowBackground === state.shadowBackground;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      elements.ratingButtons.forEach((button) => {
        const active = button.dataset.grayscaleRating === state.shadowRating;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    }

    function updateBandingReadout() {
      const focusLabel = state.focus === "10bit" ? "10-bit" : "8-bit";
      const selectedLevelLabel = getBandingLevelLabel(state.bandingLevel);
      const effective8 = getEffectiveLevels("8bit", state.bandingLevel);
      const effective10 = getEffectiveLevels("10bit", state.bandingLevel);
      const title = state.focus === "10bit"
        ? `${selectedLevelLabel} with focus on the 10-bit path.`
        : `${selectedLevelLabel} with focus on the 8-bit path.`;

      let copy = "";

      if (state.bandingLevel === "smooth") {
        copy = state.focus === "10bit"
          ? "Smooth mode lets the 10-bit reference expand toward a 1024-step ramp, which should soften contour edges and near-black transitions."
          : "Smooth mode still limits the 8-bit reference to a 256-step path, so long grayscale ramps may keep visible contour bands.";
      } else if (Number(state.bandingLevel) <= 32) {
        copy = "This is a deliberate stress test. Strong bands are expected and useful for isolating issues in the OS, GPU, cable, browser, or panel chain.";
      } else if (state.focus === "10bit") {
        copy = `The 10-bit reference maps the same preset into about ${effective10.toLocaleString()} effective steps, which should look smoother than the ${effective8}-step 8-bit side.`;
      } else {
        copy = "Mild horizontal or vertical contour lines can still be normal on an 8-bit chain. Finish the troubleshooting checklist before blaming the panel.";
      }

      if (elements.bandingProfile) {
        elements.bandingProfile.textContent = `${selectedLevelLabel} | Focus: ${focusLabel}`;
      }

      if (elements.bandingMeta) {
        elements.bandingMeta.textContent = copy;
      }

      setReadout(elements.bandingReadout, title, copy);
    }

    function updateShadowReadout() {
      const message = getShadowMessage(state.shadowRating);
      setReadout(elements.shadowReadout, message.title, message.copy);
    }

    function showResults(mode) {
      if (!elements.results) {
        return;
      }

      state.resultMode = mode;
      elements.results.hidden = false;
      elements.results.dataset.grayscaleResultsMode = mode;

      if (mode === "shadow") {
        if (elements.resultsTitle) {
          elements.resultsTitle.textContent = "Shadow detail suggestions";
        }

        if (elements.resultsCopy) {
          elements.resultsCopy.textContent = "If the 0%-4% range is missing, start with dark-scene controls such as Black Equalizer, then compare stronger low-luminance panels.";
        }
      } else {
        if (elements.resultsTitle) {
          elements.resultsTitle.textContent = "Results for gradient smoothness";
        }

        if (elements.resultsCopy) {
          elements.resultsCopy.textContent = "If banding remains obvious after SDR setup and cable checks, compare native 10-bit Mini LED and OLED panels with cleaner grayscale transitions.";
        }
      }

      elements.resultCards.forEach((card) => {
        card.hidden = card.dataset.grayscaleResultGroup !== mode;
      });

      elements.results.scrollIntoView({ behavior: "smooth", block: "start" });
      announce(mode === "shadow" ? "Shadow detail recommendations opened." : "10-bit recommendations opened.");
    }

    function closeResults() {
      if (!elements.results) {
        return;
      }

      elements.results.hidden = true;
      delete elements.results.dataset.grayscaleResultsMode;
      state.resultMode = null;
    }

    function setFocus(focus) {
      state.focus = focus === "10bit" ? "10bit" : "8bit";
      setViewerSample(0.5);
      updateBandingControls();
      updateBandingReadout();
      syncViewerState();
      renderAll();
      announce(`${state.focus === "10bit" ? "10-bit" : "8-bit"} reference focused.`);
    }

    function setBandingLevel(level) {
      state.bandingLevel = normalizeBandingLevel(level);
      setViewerSample(0.5);
      updateBandingControls();
      updateBandingReadout();
      syncViewerState();
      renderAll();
      announce(`Banding preset changed to ${getBandingLevelLabel(state.bandingLevel)}.`);
    }

    function setShadowBackground(background) {
      state.shadowBackground = normalizeShadowBackground(background);
      updateShadowControls();
      syncViewerState();
      renderAll();
      announce(`${state.shadowBackground === "lifted" ? "Lifted black" : "Pure black"} background selected.`);
    }

    function setShadowRating(rating) {
      state.shadowRating = ["excellent", "mid", "poor"].includes(rating) ? rating : "baseline";
      updateShadowControls();
      updateShadowReadout();
      announce(getShadowMessage(state.shadowRating).title);
    }

    function setFullscreenPattern(pattern) {
      state.fullscreenBandingPattern = normalizeFullscreenPattern(pattern);
      setViewerSample(0.5);
      syncViewerState();
      renderAll();
      announce(state.fullscreenBandingPattern === "horizontal-bars" ? "Fullscreen pattern changed to horizontal bars." : "Fullscreen pattern changed to vertical bars.");
    }

    function handleAction(action) {
      if (action === "jump-banding") {
        document.getElementById("grayscale-banding-test")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (action === "jump-shadow") {
        document.getElementById("grayscale-shadow-test")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (action === "fullscreen-banding") {
        toggleModuleFullscreen("banding");
        return;
      }

      if (action === "fullscreen-shadow") {
        toggleModuleFullscreen("shadow");
        return;
      }

      if (action === "close-fullscreen-viewer") {
        exitModuleFullscreen();
        return;
      }

      if (action === "show-banding-results") {
        setFocus("10bit");
        showResults("banding");
        return;
      }

      if (action === "show-shadow-results") {
        if (state.shadowRating === "baseline") {
          setShadowRating("mid");
        }

        showResults("shadow");
        return;
      }

      if (action === "close-results") {
        closeResults();
      }
    }

    function handleBandingViewerPointer(event) {
      if (state.fullscreenModule !== "banding" || !elements.fullscreenBandingCanvas) {
        return;
      }

      const rect = elements.fullscreenBandingCanvas.getBoundingClientRect();
      const useVerticalAxis = state.fullscreenBandingPattern === "horizontal-bars";
      const pointer = useVerticalAxis ? (event.clientY - rect.top) / Math.max(1, rect.height) : (event.clientX - rect.left) / Math.max(1, rect.width);

      setViewerSample(pointer);
      updateViewerRgb();
    }

    node.addEventListener("click", (event) => {
      const levelButton = event.target.closest("[data-grayscale-level]");

      if (levelButton && node.contains(levelButton)) {
        event.preventDefault();
        setBandingLevel(levelButton.dataset.grayscaleLevel);
        return;
      }

      const focusButton = event.target.closest("[data-grayscale-focus]");

      if (focusButton && node.contains(focusButton)) {
        event.preventDefault();
        setFocus(focusButton.dataset.grayscaleFocus);
        return;
      }

      const backgroundButton = event.target.closest("[data-grayscale-shadow-background]");

      if (backgroundButton && node.contains(backgroundButton)) {
        event.preventDefault();
        setShadowBackground(backgroundButton.dataset.grayscaleShadowBackground);
        return;
      }

      const ratingButton = event.target.closest("[data-grayscale-rating]");

      if (ratingButton && node.contains(ratingButton)) {
        event.preventDefault();
        setShadowRating(ratingButton.dataset.grayscaleRating);
        return;
      }

      const actionTarget = event.target.closest("[data-grayscale-action]");

      if (actionTarget && node.contains(actionTarget)) {
        event.preventDefault();
        handleAction(actionTarget.dataset.grayscaleAction);
        return;
      }

      const fullscreenTrigger = event.target.closest("[data-grayscale-fullscreen-trigger]");

      if (fullscreenTrigger && node.contains(fullscreenTrigger) && !event.target.closest("button, a, input, label, select")) {
        const moduleName = fullscreenTrigger.dataset.grayscaleFullscreenTrigger;

        if (moduleName && !isModuleFullscreen(moduleName)) {
          openModuleFullscreen(moduleName);
        }
      }
    });

    node.addEventListener("change", (event) => {
      const levelSelect = event.target.closest("[data-grayscale-fullscreen-level]");

      if (levelSelect && node.contains(levelSelect)) {
        setBandingLevel(levelSelect.value);
        return;
      }

      const focusSelect = event.target.closest("[data-grayscale-fullscreen-focus]");

      if (focusSelect && node.contains(focusSelect)) {
        setFocus(focusSelect.value);
        return;
      }

      const patternSelect = event.target.closest("[data-grayscale-fullscreen-pattern]");

      if (patternSelect && node.contains(patternSelect)) {
        setFullscreenPattern(patternSelect.value);
        return;
      }

      const shadowBackgroundSelect = event.target.closest("[data-grayscale-fullscreen-shadow-background]");

      if (shadowBackgroundSelect && node.contains(shadowBackgroundSelect)) {
        setShadowBackground(shadowBackgroundSelect.value);
      }
    });

    elements.fullscreenBandingCanvas?.addEventListener("pointermove", handleBandingViewerPointer);
    elements.fullscreenBandingCanvas?.addEventListener("pointerleave", () => {
      setViewerSample(0.5);
      updateViewerRgb();
    });

    const handleKeydown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (state.fullscreenModule) {
        exitModuleFullscreen();
        return;
      }

      if (!elements.results?.hidden) {
        closeResults();
      }
    };

    document.addEventListener("keydown", handleKeydown);

    const handleFullscreenChange = () => {
      if (getFullscreenElement() === elements.viewer) {
        return;
      }

      if (state.fullscreenModule) {
        hideViewer();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    const resizeObserver = "ResizeObserver" in window
      ? new ResizeObserver(() => {
        renderAll();
      })
      : null;

    [
      elements.banding8Canvas,
      elements.banding10Canvas,
      elements.shadowCanvas,
      elements.fullscreenBandingCanvas,
      elements.fullscreenShadowCanvas
    ].forEach((canvas) => {
      if (canvas && resizeObserver) {
        resizeObserver.observe(canvas);
      }
    });

    setViewerSample(0.5);
    updateBandingControls();
    updateShadowControls();
    updateBandingReadout();
    updateShadowReadout();
    syncViewerState();
    syncFullscreenButtons();
    renderAll();

    window.addEventListener(
      "pagehide",
      () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        document.removeEventListener("keydown", handleKeydown);
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        unlockPage();
      },
      { once: true }
    );
  }

  function initAll(root = document) {
    root.querySelectorAll("[data-grayscale-tool]").forEach(initGrayscaleTool);
  }

  initAll(document);
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
